import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import teacherService from '../../services/api/teacher';
import { 
  MessageSquare, 
  Send, 
  Search, 
  CheckCheck, 
  Reply, 
  Trash2, 
  Mail, 
  MailOpen, 
  X, 
  AlertCircle, 
  Zap,
  ShieldCheck,
  Plus,
  MessageCircle,
  Star,
  Loader
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useConfirm } from '../../hooks/useConfirm';
import { useToast } from '../../components/common/Toast';
import { getImageUrl } from '../../config/apiConfig';

const TeacherMessages = () => {
  const [selectedThreadUserId, setSelectedThreadUserId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [composeMode, setComposeMode] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [starredMessageIds, setStarredMessageIds] = useState(new Set());
  const [newMessage, setNewMessage] = useState({
    recipient_id: '',
    subject: '',
    message: '',
    priority: 'normal'
  });

  const { confirm, ConfirmDialogComponent } = useConfirm();
  const toast = useToast();
  const queryClient = useQueryClient();

  // جلب قائمة المحادثات
  const { data: threadsData, isLoading: threadsLoading, error: threadsError } = useQuery(
    'messageThreads',
    () => teacherService.getMessageThreads(),
    { retry: 1 }
  );

  // جلب محادثة محددة
  const { data: conversationData, isLoading: conversationLoading } = useQuery(
    ['conversation', selectedThreadUserId],
    () => selectedThreadUserId ? teacherService.getConversationThread(selectedThreadUserId) : null,
    { 
      enabled: !!selectedThreadUserId,
      retry: 1 
    }
  );


  // جلب قائمة الطلاب للرسالة الجديدة
  const { data: studentsData } = useQuery(
    'teacherStudents',
    () => teacherService.getStudentsList(),
    { 
      enabled: composeMode,
      retry: 1
    }
  );

  const filteredStudents = studentsData?.students?.filter(student => 
    student.name?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(studentSearchTerm.toLowerCase())
  ) || [];

  // إغلاق القائمة عند الضغط خارجها
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (composeMode && showStudentDropdown) {
        const dropdown = document.getElementById('student-dropdown');
        const input = document.getElementById('student-search-input');
        if (dropdown && !dropdown.contains(e.target) && input && !input.contains(e.target)) {
          setShowStudentDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [composeMode, showStudentDropdown]);

  // Mutation للرد
  const sendMessageMutation = useMutation(
    ({ messageId, replyData }) => teacherService.replyToMessage(messageId, replyData),
    {
      onSuccess: () => {
        toast.success('تم إرسال ردك بنجاح');
        queryClient.invalidateQueries(['conversation', selectedThreadUserId]);
        queryClient.invalidateQueries('messageThreads');
        setReplyText('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في إرسال الرد');
      }
    }
  );

  // Mutation لإرسال رسالة جديدة
  const sendNewMessageMutation = useMutation(
    (messageData) => teacherService.sendMessage(messageData),
    {
      onSuccess: () => {
        toast.success('تم إرسال الرسالة بنجاح');
        queryClient.invalidateQueries('messageThreads');
        setComposeMode(false);
        setNewMessage({ recipient_id: '', subject: '', message: '', priority: 'normal' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في إرسال الرسالة');
      }
    }
  );

  // Mutation لحذف رسالة
  const deleteMessageMutation = useMutation(
    (messageId) => teacherService.deleteMessage(messageId),
    {
      onSuccess: () => {
        toast.success('تم حذف الرسالة بنجاح');
        queryClient.invalidateQueries(['conversation', selectedThreadUserId]);
        queryClient.invalidateQueries('messageThreads');
      },
      onError: () => {
        toast.error('حدث خطأ أثناء محاولة الحذف');
      }
    }
  );

  // Mutation لتثبيت الرسالة
  const starMessageMutation = useMutation(
    (messageId) => teacherService.starMessage(messageId),
    {
      onSuccess: (data, messageId) => {
        setStarredMessageIds(prev => new Set([...prev, messageId]));
        toast.success('تم تثبيت الرسالة');
      }
    }
  );

  // Mutation لإزالة التثبيت
  const unstarMessageMutation = useMutation(
    (messageId) => teacherService.unstarMessage(messageId),
    {
      onSuccess: (data, messageId) => {
        setStarredMessageIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
        toast.success('تم إزالة التثبيت');
      }
    }
  );

  // Effect لبث حالة الكتابة
  React.useEffect(() => {
    if (!selectedThreadUserId || !replyText) return;

    const typingTimer = setInterval(() => {
      teacherService.updateTypingStatus(selectedThreadUserId, !!replyText)
        .catch(err => {
          // Typing status error handling
        });
    }, 1000);

    return () => clearInterval(typingTimer);
  }, [selectedThreadUserId, replyText]);

  // Effect لجلب حالة الكتابة للطالب
  React.useEffect(() => {
    if (!selectedThreadUserId) return;

    const checkTyping = setInterval(async () => {
      try {
        const result = await teacherService.getTypingStatus(selectedThreadUserId);
        setTypingUsers(result.typingUsers || []);
      } catch (error) {
        // Get typing status error handling
      }
    }, 1000);

    return () => clearInterval(checkTyping);
  }, [selectedThreadUserId]);

  // تصفية المحادثات
  const filteredThreads = (threadsData?.threads || []).filter(thread =>
    thread.other_user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    thread.last_message_preview?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    thread.last_message_subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'أمس';
    } else {
      return getArabicDate(dateString);
    }
  };

  const handleReply = (lastMessageId) => {
    if (!replyText.trim()) return;
    sendMessageMutation.mutate({
      messageId: lastMessageId,
      replyData: { message: replyText }
    });
  };

  const handleDeleteConversation = async () => {
    const confirmed = await confirm({
      title: 'حذف المحادثة',
      message: `هل أنت متأكد من رغبتك في حذف جميع الرسائل مع ${currentThread?.other_user_name}؟`,
      confirmText: 'حذف',
      cancelText: 'إلغاء',
      isDangerous: true
    });

    if (confirmed) {
      try {
        await teacherService.deleteConversation(selectedThreadUserId);
        toast.success('تم حذف المحادثة بنجاح');
        setSelectedThreadUserId(null);
        queryClient.invalidateQueries('messageThreads');
      } catch (error) {
        toast.error('فشل حذف المحادثة');
        // Delete conversation error handling
      }
    }
  };

  const handleDelete = async (messageId) => {
    const confirmed = await confirm({
      title: 'تأكيد الحذف',
      message: 'هل أنت متأكد من حذف هذه الرسالة؟',
      type: 'danger'
    });
    
    if (confirmed) {
      deleteMessageMutation.mutate(messageId);
    }
  };

  if (threadsError) {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center">
        <div className="bg-red-50 p-10 rounded-[2.5rem] border border-red-100 shadow-sm">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">خطأ</h2>
          <p className="text-gray-600 mb-8">فشل في تحميل الرسائل</p>
          <button onClick={() => window.location.reload()} className="bg-red-600 text-white px-10 py-3 rounded-2xl font-bold hover:bg-red-700 transition-all">
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  const currentThread = threadsData?.threads?.find(t => t.other_user_id === selectedThreadUserId);
  const messages = conversationData?.messages || [];


  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in duration-500">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <MessageSquare className="text-primary-600" size={32} />
            صندوق الرسائل
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2">
            <ShieldCheck className="text-emerald-500" size={18} />
            حوارات مباشرة مع طلابك
          </p>
        </div>
        
        <button
          onClick={() => setComposeMode(true)}
          className="bg-primary-600 text-white px-8 py-4 rounded-2xl font-black text-sm shadow-lg shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95 flex items-center justify-center gap-3 whitespace-nowrap"
        >
          <Plus size={20} />
          رسالة جديدة
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {[
          { label: 'إجمالي المحادثات', value: threadsData?.threads?.length || 0, icon: MessageCircle, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'محادثات جديدة', value: threadsData?.threads?.filter(t => t.is_unread).length || 0, icon: Zap, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'رسائل إجمالي', value: threadsData?.threads?.reduce((acc, t) => acc + t.message_count, 0) || 0, icon: CheckCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' }
        ].map((stat, i) => (
          <div key={i} className={`bg-white p-6 rounded-[2rem] border shadow-sm flex items-center gap-5 group hover:shadow-md transition-all border-gray-100`}>
            <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>
              <stat.icon size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">{stat.label}</p>
              <p className="text-3xl font-black text-gray-900 leading-none">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Compose Modal */}
      {composeMode && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300">
            <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-primary-100/50">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                  <Send className="text-primary-600" size={28} />
                  رسالة جديدة
                </h3>
                <button
                  onClick={() => setComposeMode(false)}
                  className="p-2 hover:bg-white/50 rounded-xl transition-colors text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">المستقبل *</label>
                <div className="relative">
                  <input
                    id="student-search-input"
                    type="text"
                    value={studentSearchTerm}
                    onChange={(e) => {
                      setStudentSearchTerm(e.target.value);
                      setShowStudentDropdown(true);
                    }}
                    onFocus={() => setShowStudentDropdown(true)}
                    placeholder="ابحث عن الطالب..."
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium text-sm"
                  />
                  
                  {showStudentDropdown && (filteredStudents.length > 0 || !studentSearchTerm) && (
                    <div id="student-dropdown" className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-lg max-h-64 overflow-y-auto z-50">
                      {filteredStudents.length > 0 ? (
                        filteredStudents.map((student) => (
                          <button
                            key={student.id}
                            onClick={() => {
                              setNewMessage({...newMessage, recipient_id: student.id});
                              setStudentSearchTerm(student.name);
                              setShowStudentDropdown(false);
                            }}
                            className="w-full text-right px-5 py-3 hover:bg-primary-50 transition-colors border-b border-gray-50 last:border-b-0 flex items-center justify-between"
                          >
                            <div className="flex-1">
                              <p className="font-bold text-gray-900">{student.name}</p>
                              <p className="text-xs text-gray-400">{student.email}</p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-black">
                              {student.name?.charAt(0)}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-400">جاري التحميل...</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">الموضوع *</label>
                <input
                  type="text"
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
                  placeholder="موضوع الرسالة..."
                  className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">المحتوى *</label>
                <textarea
                  value={newMessage.message}
                  onChange={(e) => setNewMessage({...newMessage, message: e.target.value})}
                  rows={6}
                  placeholder="اكتب رسالتك..."
                  className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium resize-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => {
                    if (!newMessage.recipient_id) {
                      toast.error('اختر طالباً');
                      return;
                    }
                    if (!newMessage.subject || !newMessage.message) {
                      toast.error('أكمل البيانات');
                      return;
                    }
                    sendNewMessageMutation.mutate(newMessage);
                  }}
                  disabled={sendNewMessageMutation.isLoading}
                  className="flex-1 bg-primary-600 text-white px-8 py-4 rounded-2xl font-black shadow-lg shadow-primary-100 hover:bg-primary-700 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  <Send size={18} /> إرسال
                </button>
                <button
                  onClick={() => {
                    setComposeMode(false);
                    setStudentSearchTerm('');
                    setNewMessage({ recipient_id: '', subject: '', message: '', priority: 'normal' });
                  }}
                  className="px-8 py-4 rounded-2xl font-black text-gray-600 hover:bg-gray-100 transition-all"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left: Threads List */}
        <div className="lg:col-span-4 space-y-6 flex flex-col">
          <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">
            <div className="relative group">
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 w-5 h-5" />
              <input
                type="text"
                placeholder="ابحث عن محادثة..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3.5 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
              />
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden flex-1 min-h-[500px]">
            <div className="overflow-y-auto max-h-[600px]">
              {threadsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="small" />
                </div>
              ) : filteredThreads.length === 0 ? (
                <div className="py-24 text-center">
                  <Mail className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-400 font-bold px-10">لا توجد محادثات</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {filteredThreads.map((thread) => (
                    <button
                      key={thread.other_user_id}
                      onClick={() => setSelectedThreadUserId(thread.other_user_id)}
                      className={`w-full p-6 text-right transition-all ${selectedThreadUserId === thread.other_user_id ? 'bg-primary-50/50 border-r-4 border-primary-600' : 'hover:bg-gray-50'}`}
                    >
                      <div className="flex gap-4 items-start">
                        <div className="flex-shrink-0 relative">
                          {thread.other_user_avatar ? (
                            <img
                              src={getImageUrl(thread.other_user_avatar)}
                              alt={thread.other_user_name}
                              className="w-12 h-12 rounded-2xl object-cover shadow-lg border-2 border-white"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-black shadow-lg border-2 border-white">
                              {thread.other_user_name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                          {thread.is_unread && (
                            <div className="absolute -top-1 -left-1 w-4 h-4 bg-amber-500 rounded-full border-2 border-white animate-pulse"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className={`text-sm truncate ${thread.is_unread ? 'font-black text-gray-900' : 'font-bold text-gray-600'}`}>
                              {thread.other_user_name}
                            </p>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                              {formatTime(thread.last_message_time)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate mb-2">
                            {thread.message_count} رسالة
                          </p>
                          <p className="text-[10px] text-gray-400 truncate">
                            {thread.last_message_preview}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Conversation */}
        <div className="lg:col-span-8">
          {selectedThreadUserId ? (
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 flex flex-col h-full animate-in fade-in slide-in-from-left duration-500">
              
              {/* Header */}
              <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                <div className="flex items-center gap-4">
                  {currentThread?.other_user_avatar ? (
                    <img
                      src={getImageUrl(currentThread.other_user_avatar)}
                      alt={currentThread.other_user_name}
                      className="w-14 h-14 rounded-3xl object-cover shadow-xl border-4 border-white"
                    />
                  ) : (
                    <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-3xl flex items-center justify-center text-white font-black shadow-xl border-4 border-white">
                      {currentThread?.other_user_name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-black text-gray-900 leading-none mb-2">
                      {currentThread?.other_user_name}
                    </h3>
                    {typingUsers.length > 0 ? (
                      <div className="flex items-center gap-2">
                        <Loader size={12} className="animate-spin text-primary-500" />
                        <p className="text-[10px] font-black text-primary-500 uppercase tracking-widest">
                          يكتب...
                        </p>
                      </div>
                    ) : (
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        {currentThread?.message_count} رسالة
                      </p>
                    )}
                  </div>
                </div>

                {/* Delete Conversation Button */}
                <button
                  onClick={handleDeleteConversation}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                  title="حذف المحادثة"
                >
                  <Trash2 size={20} className="text-gray-400 group-hover:text-red-500 transition-colors" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-8 space-y-6">
                {conversationLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <LoadingSpinner size="small" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MailOpen className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                    <p className="text-gray-400">لا توجد رسائل</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className={`flex gap-3 ${msg.is_sent ? 'justify-start' : 'justify-end'}`}>
                      {msg.is_sent && (
                        <div className="flex-shrink-0">
                          {msg.other_user_avatar ? (
                            <img
                              src={getImageUrl(msg.other_user_avatar)}
                              alt={msg.other_user_name}
                              className="w-8 h-8 rounded-lg object-cover border-2 border-gray-200"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-xs font-bold border-2 border-gray-200">
                              {msg.other_user_name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                      )}
                      <div className={`max-w-xs lg:max-w-md px-6 py-4 rounded-[2rem] shadow-sm group ${
                        msg.is_sent ? 'bg-gray-100 text-gray-900' : 'bg-primary-600 text-white'
                      }`}>
                        <p className="text-sm font-medium mb-2">{msg.subject}</p>
                        <p className={`text-sm leading-relaxed ${msg.is_sent ? 'text-gray-700' : 'text-primary-50'}`}>
                          {msg.message}
                        </p>
                        <div className="flex items-center justify-between mt-3 gap-4">
                          <p className={`text-[10px] font-bold uppercase tracking-widest ${msg.is_sent ? 'text-gray-500' : 'text-primary-200'}`}>
                            {formatTime(msg.created_at)}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                if (starredMessageIds.has(msg.id)) {
                                  unstarMessageMutation.mutate(msg.id);
                                } else {
                                  starMessageMutation.mutate(msg.id);
                                }
                              }}
                              className={`transition-colors opacity-0 group-hover:opacity-100 ${
                                starredMessageIds.has(msg.id)
                                  ? msg.is_sent ? 'text-amber-500' : 'text-amber-300'
                                  : msg.is_sent ? 'text-gray-400 hover:text-amber-500' : 'text-primary-200 hover:text-amber-300'
                              }`}
                              title={starredMessageIds.has(msg.id) ? 'إزالة التثبيت' : 'تثبيت الرسالة'}
                            >
                              <Star size={14} fill={starredMessageIds.has(msg.id) ? 'currentColor' : 'none'} />
                            </button>
                            {msg.is_sent && (
                              <button
                                onClick={() => handleDelete(msg.id)}
                                className={`text-gray-400 hover:text-red-500 transition-colors ${msg.is_sent ? '' : 'hidden'}`}
                                title="حذف"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Reply Form */}
              {messages.length > 0 && (
                <div className="p-8 border-t border-gray-50 bg-gray-50/30">
                  <div className="flex items-center gap-2 mb-4 text-primary-600 font-black text-sm uppercase tracking-widest">
                    <Reply size={16} /> رد سريع
                  </div>
                  <div className="flex gap-3 items-end">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="اكتب ردك..."
                      rows={3}
                      className="flex-1 p-4 bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-medium resize-none"
                    />
                    <button
                      onClick={() => handleReply(messages[messages.length - 1].id)}
                      disabled={!replyText.trim() || sendMessageMutation.isLoading}
                      className="bg-primary-600 text-white p-4 rounded-2xl font-black hover:bg-primary-700 transition-all shadow-lg shadow-primary-100 flex items-center justify-center disabled:opacity-50"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-[3rem] border border-gray-100 shadow-sm p-20 flex flex-col items-center justify-center text-center h-full">
              <div className="bg-gray-50 w-32 h-32 rounded-full flex items-center justify-center mb-8 shadow-inner">
                <MailOpen className="w-16 h-16 text-gray-200" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-2">اختر محادثة</h3>
              <p className="text-gray-400 max-w-xs font-medium">
                اختر من قائمة المحادثات على اليسار لعرض التفاصيل
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialogComponent />
    </div>
  );
};

export default TeacherMessages;
